import React, { useState, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './ForgotPassword.module.css';
import Footer from '../../common/UI/Footer';
import { useNavigate } from 'react-router-dom';
import { useForgotPassword } from '../../hooks/useForgetpassword';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const { sendResetLink, isLoading, error } = useForgotPassword();
  const navigate = useNavigate();

  // Handle error notifications
  React.useEffect(() => {
    if (error) {
      const errorMessage = error?.response?.data?.message || 'An error occurred while sending reset link';
      toast.error(errorMessage);
      setErrors(prev => ({ ...prev, submit: errorMessage }));
    }
  }, [error]);

  const validateForm = useCallback(() => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    return newErrors;
  }, [email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();

    if (Object.keys(newErrors).length === 0) {
      try {
        await sendResetLink(
          email.trim(),
          {
            onSuccess: () => {
              toast.success('Password reset link sent! Please check your email.');
              setEmail('');
              setTimeout(() => {
                navigate('/signin');
              }, 3000);
            }
          }
        );
      } catch (err) {
        console.error('Error sending reset link:', err);
      }
    } else {
      setErrors(newErrors);
      toast.error('Please fix the errors in the form');
    }
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (errors.email || errors.submit) {
      setErrors({});
    }
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <div className={styles.container}>
        <div className={styles.formCard}>
          <h2>Forgot Password</h2>
          <p className={styles.instructions}>
            Enter your email address below and we'll send you a link to reset your password.
          </p>
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.inputGroup}>
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={handleChange}
                className={`${errors.email || errors.submit ? styles.errorInput : ''}`}
                disabled={isLoading}
                autoComplete="email"
              />
              {errors.email && <span className={styles.errorText}>{errors.email}</span>}
              {errors.submit && <span className={styles.errorText}>{errors.submit}</span>}
            </div>

            <button
              type="submit"
              className={`${styles.submitButton} ${isLoading ? styles.loading : ''}`}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className={styles.loadingWrapper}>
                  <Loader2 className={styles.spinner} size={20} />
                  Sending...
                </span>
              ) : (
                'Send Reset Link'
              )}
            </button>

            <p className={styles.loginLink}>
              Remember your password?{' '}
              <span 
                className={styles.signInLink}
                onClick={() => navigate('/signin')}
              >
                Back to Sign in
              </span>
            </p>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ForgotPassword;